import {Injectable} from '@angular/core';

import {SitesRestService} from '../apis/sites-rest.service';
import {LinksRestService} from '../apis/links-rest.service';
import {TenantsRestService} from '../apis/tenants-rest.service';
import {DevicesRestService} from '../apis/devices-rest.service';
import {EndpointsRestService} from '../apis/endpoints-rest.service';

import {cloneDeep, extend, isNil, isObject} from 'lodash';
import {RequestOptions} from 'ac-infra';
import {GroupsRestService} from '../apis/groups-rest.service';
import {NetworkSearchService} from '../network-search.service';
import {MetadataService} from '../../../metadata/metadata.service';
import {RestResponseSuccess} from '../../../common/server-actions/rest';
import {MiscellaneousJSUtilsService} from '../../../common/services/miscellaneous-JS-utils.service';
import {ChannelsRestService} from '../apis/channels-rest.service';
import {LCCustomersRestService} from '../apis/lc-customers-rest.service';


@Injectable({providedIn: 'root'})
export class NetworkDataService {

    private audioCodesDevicesList: any;
    private linkTypeList: any;

    constructor(private miscellaneousJSUtilsService: MiscellaneousJSUtilsService,
                private groupsRestService: GroupsRestService,
                private channelsRestService: ChannelsRestService,
                private lcCustomersRestService: LCCustomersRestService,
                private networkSearchService: NetworkSearchService,
                private tenantsRestService: TenantsRestService,
                private devicesRestService: DevicesRestService,
                private sitesRestService: SitesRestService,
                private linksRestService: LinksRestService,
                private endpointsRestService: EndpointsRestService) {
    }


    // For Network Topology / Map Shapes (Devices & Sites)
    public getDevicesAndSitesForMap = (success, search, ids?) => {

        const dataObject: any = {
            filteredDevices: this.devicesRestService.getFilteredEntitiesHashed(ids),
            filteredSites: this.sitesRestService.getFilteredEntitiesHashed(ids),
            devices: cloneDeep(this.devicesRestService.getAllEntities(ids)),
            sites: cloneDeep(this.sitesRestService.getAllEntities(ids))
        };

        dataObject.devices.forEach((device: any) => {
            device.filtered = !!dataObject.filteredDevices[device.id];
        });

        dataObject.sites.forEach((site: any) => {
            site.filtered = !!dataObject.filteredSites[site.id];
        });

        this.networkSearchService.searchEntity(dataObject.devices, search, 'devices');
        this.networkSearchService.searchEntity(dataObject.sites, search, 'sites');

        success({
            devices: dataObject.devices,
            sites: dataObject.sites
        });
    };

    // For Network Topology / Map Connections (Links)
    public getLinksForMap = (success, search, ids) => {

        const dataObject: any = {
            filteredLinks: cloneDeep(this.linksRestService.getFilteredEntitiesHashed(ids)),
            links: cloneDeep(this.linksRestService.getAllEntities(ids))
        };

        dataObject.links.forEach((link: any) => {
            link.filtered = !!dataObject.filteredLinks[link.id];
        });

        this.networkSearchService.searchEntity(dataObject.links, search, 'links');

        success({links: dataObject.links});
    };

    // For Device Page (Devices - Managed Table)
    public getDevicesForGrid = (requestOptions: RequestOptions, callback) => {
        this.audioCodesDevicesList = MetadataService.getType('AudioCodesDevices');
        const onSuccess = (response: RestResponseSuccess) => {
            const devices = response.data.devices;
            if (devices.length === 0) {
                return callback(response.data);
            }
            const filterIds = {
                filter: {
                    id: devices.map((device) => device.id)
                }
            };

            this.devicesRestService.getMonitorStatisticsByUnitID({
                success: (entityStatistics: RestResponseSuccess) => {
                    const mappedStatistics: any = this.miscellaneousJSUtilsService.arrayToObjectByKey(entityStatistics.data.entityStatistics, 'id');

                    devices.forEach((device: any) => {
                        const productTypeObject = this.audioCodesDevicesList[device.productType] || {};

                        if (['ACL', 'CLOUDBOND'].indexOf(productTypeObject.familyType) > -1) {
                            device.type = 'ACL';
                        } else if (['LYNC'].indexOf(productTypeObject.familyType) > -1) {
                            device.type = 'LYNC';
                        }

                        device.productTypeName = productTypeObject.viewName;

                        extend(device, (mappedStatistics[device.id] || {}));
                    });

                    callback(response.data);
                },
                failure: onFailure,
                parameters: filterIds,
                uri: 'devicesEntities'
            });
        };

        const onFailure = () => {
            callback({devices: [], pages: {total: 0, totalElements: 0}});
        };

        this.devicesRestService.getFilteredEntitiesForTable({
            ...requestOptions,
            singletonGroupName: 'manage'
        }, onSuccess, onFailure);
    };

    // For Links Page (Links - Table)
    public getLinksForGrid = (parameters, callback, filteredEntity) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const links = response.data.links;
            if (links.length === 0) {
                return callback(response.data);
            }
            const filterIds = {
                filter: {
                    id: links.map((link) => link.id)
                }
            };

            this.linksRestService.getMonitorStatisticsByUnitID({
                success: (entityStatistics: RestResponseSuccess) => {
                    const mappedStatistics: any = this.miscellaneousJSUtilsService.arrayToObjectByKey(entityStatistics.data.entityStatistics, 'id');

                    links.forEach((link: any) => {
                        link.category = isObject(link.type) ? this.buildLinkCategory(link) : '';
                        extend(link, (mappedStatistics[link.id] || {}));
                    });

                    callback(response.data);
                },
                failure: onFailure,
                parameters: filterIds,
                uri: 'linksEntities'
            });
        };

        const onFailure = () => {
            callback({links: [], pages: {total: 0, totalElements: 0}});
        };

        this.linksRestService.getFilteredEntitiesForTable({
            parameters,
            filteredEntity,
            singletonGroupName: 'link'
        }, onSuccess, onFailure);
    };

    public getLCCustomersForGrid = (parameters, callback) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const emptyObj: any = {pages: {total: 0, totalElements: 0}, hash: {}, lcCustomers: []};
            if (!response.data || !response.data.lcCustomers || response.data.lcCustomers.length === 0) {
                return callback(isNil(response) ? response : emptyObj);
            }

            callback(response.data);
        };

        const onFailure = () => {
            callback({lcCustomers: [], pages: {total: 0, totalElements: 0}});
        };

        this.lcCustomersRestService.getFilteredEntitiesForTable({
            parameters,
            filteredEntity: 'lcCustomers',
            singletonGroupName: 'lcCustomers'
        }, onSuccess, onFailure, 'lcCustomers');
    };

    public getChannelForGrid = (parameters, callback) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const emptyObj: any = {pages: {total: 0, totalElements: 0}, hash: {}, channels: []};
            if (!response.data || !response.data.channels || response.data.channels.length === 0) {
                return callback(isNil(response) ? response : emptyObj);
            }

            callback(response.data);
        };

        const onFailure = () => {
            callback({channels: [], pages: {total: 0, totalElements: 0}});
        };

        this.channelsRestService.getFilteredEntitiesForTable({
            parameters,
            filteredEntity: 'channel',
            singletonGroupName: 'channel'
        }, onSuccess, onFailure, 'channel');
    };

    public getGroupsForGrid = (parameters, type, callback, url) => {
        const entityType = type === 'SERVICE' ? 'services' : 'groups';
        const onSuccess = (response: RestResponseSuccess) => {
            const emptyObj = {pages: {total: 0, totalElements: 0}, hash: {}};
            emptyObj[entityType] = [];
            if (!response.data || !response.data[entityType] || response.data[entityType].length === 0) {
                return callback(isNil(response) ? response : emptyObj);
            }

            callback(response.data);
        };

        const onFailure = () => {
            callback({groups: [], pages: {total: 0, totalElements: 0}});
        };

        const lcCustomerId = parameters?.filter?.lcCustomerId;
        const groupType = type === 'SERVICE' ? 'services' : type.toLowerCase() + 'Groups';
        this.groupsRestService.getFilteredEntitiesForTable({
            parameters,
            url,
            filteredEntity: lcCustomerId ? undefined : groupType,
            singletonGroupName: 'groups',
        }, onSuccess, onFailure, entityType);
    };

    // For Tenants Page (Tenants - Table)
    public getTenantsForGrid = (parameters, callback) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const emptyObj: any = {pages: {total: 0, totalElements: 0}, hash: {}, tenants: []};
            if (!response.data || !response.data.tenants || response.data.tenants.length === 0) {
                return callback(isNil(response) ? response : emptyObj);
            }

            callback(response.data);
        };

        const onFailure = () => {
            callback({tenants: [], pages: {total: 0, totalElements: 0}});
        };

        this.tenantsRestService.getFilteredEntitiesForTable({
            parameters,
            filteredEntity: 'networkTenants'
        }, onSuccess, onFailure);
    };

    // For Sites Page (Sites - Table)
    public getSitesForGrid = (parameters, callback) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const sites = response.data.sites;
            if (sites.length === 0) {
                return callback(response.data);
            }
            const filterIds = {
                filter: {
                    id: sites.map((site) => site.id)
                }
            };

            this.getMonitorStatistics(this.sitesRestService, sites, callback, response, onFailure, filterIds, 'sitesEntities');
        };

        const onFailure = () => {
            callback({sites: [], pages: {total: 0, totalElements: 0}});
        };

        this.sitesRestService.getFilteredEntitiesForTable({parameters, singletonGroupName: 'sites'}, onSuccess, onFailure);
    };

    // For Endpoints Page (Endpoints - Table)
    public getEndpointsForGrid = (parameters, callback) => {
        const onSuccess = (response: RestResponseSuccess) => {
            const endpoints = response.data.endpoints;
            const filterIds: any = {filter: {}};

            const ids = endpoints.map((endpoint) => endpoint.id);
            if (ids.length > 0) {
                filterIds.filter.id = ids;
            }

            this.getMonitorStatistics(this.endpointsRestService, endpoints, callback, response, onFailure, filterIds, 'endpointsEntities');
        };

        const onFailure = () => {
            callback({endpoints: [], pages: {total: 0, totalElements: 0}});
        };

        this.endpointsRestService.getFilteredEndpoints({
            success: onSuccess,
            failure: onFailure,
            parameters,
            skipPopulateFilter: false,
            singletonGroupName: 'endpoints',
        });
    };

    private getMonitorStatistics = (entityService, entities, callback, response, onFailure, filterIds, uri = undefined) => {
        entityService.getMonitorStatisticsByUnitID({
            success: (entityStatistics: RestResponseSuccess) => {
                const mappedStatistics: any = this.miscellaneousJSUtilsService.arrayToObjectByKey(entityStatistics.data.entityStatistics, 'id');

                entities.forEach((entity: any) => {
                    entity.managementStatus = this.flattenApplicationStatuses(entity, 'management', 'managementStatus') || 'UNMONITORED';
                    entity.voiceQualityStatus = this.flattenApplicationStatuses(entity, 'voiceQuality', 'voiceQualityStatus') || 'UNMONITORED';

                    extend(entity, (mappedStatistics[entity.id] || {}));
                });

                callback(response.data);
            },
            failure: onFailure,
            parameters: filterIds,
            uri
        });
    };

    private flattenApplicationStatuses = (entity, categoryField, statusField) => entity.applicationsStatus &&
        entity.applicationsStatus[categoryField] &&
        entity.applicationsStatus[categoryField][statusField];

    private buildLinkCategory = (link: any) => {
        this.linkTypeList = MetadataService.getType('LinkType');
        const linkType = (this.linkTypeList[link.type.categoryType] || {}).viewName || '';
        const linkValue = link.type.categorySecondaryValue ? ' ' + link.type.categorySecondaryValue : '';

        return linkType + ' ' + link.type.categoryValue + linkValue;
    };
}
